// ── TourOverlay — interaktiver In-App-Rundgang (Coach-Marks) ───────────────────
// Mehrstufige Tour: jeder Schritt dunkelt die App ab, setzt einen Spotlight auf
// ein ECHTES UI-Element (per data-tour-Attribut) und wechselt ggf. vorher den Tab,
// damit der passende Screen sichtbar ist. Optik = identisch zum bisherigen
// Onboarding-Overlay (Surface-Karte, red.-Wortmarke + Schritt-Marker, Editorial-
// Headline, roter CTA). Zeigt sich genau einmal (Gate: red_tutorial_seen in App.js).

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { F } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';

// Tour-Stops. target = data-tour-Selektor des Elements, tab = vorher dahin wechseln.
// Schritte ohne target werden mittig + voll abgedunkelt gezeigt (Intro/Outro).
const STEPS = [
  { key: 'welcome' },
  { key: 'overview',     tab: 'dashboard', target: '[data-tour="nav-dashboard"]' },
  { key: 'transactions', tab: 'wallet',    target: '[data-tour="nav-wallet"]' },
  { key: 'add',                            target: '[data-tour="nav-fab-add"]' },
  { key: 'reports',      tab: 'reports',   target: '[data-tour="nav-reports"]' },
  { key: 'privacy' },
];

const PAD = 8; // Spotlight-Rand um das Element herum

export default function TourOverlay({ onNavigate, onDone, isWide }) {
  const { T } = useTheme();
  const { t } = useT();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  // onNavigate ist im Parent nicht memoisiert → bei jedem Render neue Referenz.
  // In eine Ref packen, damit der Schritt-Effekt NICHT bei jedem Parent-Render neu
  // feuert (sonst bricht die Spotlight-Messung immer wieder ab → kein Icon leuchtet).
  const navRef = useRef(onNavigate);
  useEffect(() => { navRef.current = onNavigate; }, [onNavigate]);

  const step = STEPS[i];
  const isLast = i === STEPS.length - 1;

  const measure = useCallback(() => {
    const s = STEPS[i];
    if (!s.target) { setRect(null); return; }
    const el = document.querySelector(s.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [i]);

  // Schrittwechsel: Spotlight clearen, ggf. Tab wechseln, dann Ziel vermessen.
  // Bewusst NUR von [i, measure] abhängig (NICHT onNavigate) → läuft genau einmal
  // pro Schritt, die Messung wird nicht mehr abgebrochen.
  useEffect(() => {
    setRect(null);
    const s = STEPS[i];
    if (s.tab && navRef.current) navRef.current(s.tab);
    let raf1, raf2, tid;
    raf1 = requestAnimationFrame(() => {
      tid = setTimeout(() => {
        measure();
        raf2 = requestAnimationFrame(measure);
      }, 120);
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); clearTimeout(tid); };
  }, [i, measure]);

  // Bei Resize/Scroll Spotlight neu ausrichten.
  useEffect(() => {
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => { window.removeEventListener('resize', onMove); window.removeEventListener('scroll', onMove, true); };
  }, [measure]);

  const next = () => { if (isLast) onDone?.(); else setI(i + 1); };
  const skip = () => onDone?.();

  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const targetBelowMid = rect ? (rect.top + rect.height / 2) > vh / 2 : false;

  // Karte in die GEGEN-Hälfte des Ziels legen → kein Overlap mit dem Spotlight.
  const cardPos = !rect
    ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    : targetBelowMid
      ? { bottom: vh - rect.top + 16, left: '50%', transform: 'translateX(-50%)' }
      : { top: rect.top + rect.height + 16, left: '50%', transform: 'translateX(-50%)' };

  const num = String(i + 1).padStart(2, '0');

  const overlay = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      {/* 1) Transparenter Klick-Blocker — App-Interaktion während der Tour sperren */}
      <div style={{ position: 'absolute', inset: 0, background: 'transparent' }} />

      {/* 2) Spotlight (sein riesiger Schatten = die Abdunklung) ODER volle Abdunklung */}
      {rect ? (
        <motion.div
          key={`spot-${i}`}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
          style={{
            position: 'absolute',
            top: rect.top - PAD, left: rect.left - PAD,
            width: rect.width + PAD * 2, height: rect.height + PAD * 2,
            borderRadius: 16,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.80)',
            outline: '2px solid rgba(255,255,255,0.9)',
            pointerEvents: 'none',
          }}
        />
      ) : (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.80)' }} />
      )}

      {/* 3) Karte — gleiche Optik wie das bisherige Onboarding-Overlay */}
      {/* Positionierungs-Wrapper (hält die Zentrierung) — getrennt von der Motion,
          weil framers y-Animation sonst das transform: translate(...) überschreibt. */}
      <div style={{ position: 'absolute', width: 'calc(100% - 40px)', maxWidth: isWide ? 360 : 340, ...cardPos }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={`card-${i}`}
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          style={{
            borderRadius: 20,
            padding: '24px 22px 18px',
            background: T.surface,
            border: `1px solid ${T.line}`,
            boxShadow: T.cardShadowLg,
            color: T.text,
          }}
        >
          {/* Header: red. + Schritt-Marker */}
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            paddingBottom: 12, marginBottom: 18, borderBottom: `1px solid ${T.lineSoft}`,
          }}>
            <div style={{ fontFamily: F.family, fontSize: 15, fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1, color: T.text }}>
              red<span style={{ color: T.red }}>.</span>
            </div>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mute }}>
              {num} — {t('tour_marker')}
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.red, marginBottom: 10 }}>
            {t(`tour_${step.key}_eyebrow`)}
          </div>
          <div style={{ fontFamily: F.display, fontSize: 27, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.14, marginBottom: 12, color: T.text }}>
            {t(`tour_${step.key}_title`)}
          </div>
          <div style={{ fontSize: 13.5, color: T.sub, lineHeight: 1.55, letterSpacing: '-0.005em', marginBottom: 22 }}>
            {t(`tour_${step.key}_body`)}
          </div>

          {/* Footer: Überspringen · Dots · Weiter/Los geht's */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <button onClick={skip} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: F.family, fontSize: 12.5, fontWeight: 600, color: T.mute, padding: '8px 2px',
            }}>
              {t('tour_skip')}
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              {STEPS.map((_, idx) => (
                <div key={idx} style={{ width: idx === i ? 18 : 6, height: 6, borderRadius: 99, background: idx === i ? T.red : T.line, transition: 'all 0.2s' }} />
              ))}
            </div>
            <button onClick={next} style={{
              background: T.redGradient, border: 'none', borderRadius: 14,
              padding: '12px 18px', cursor: 'pointer', color: '#fff',
              fontFamily: F.family, fontWeight: 700, fontSize: 12.5, letterSpacing: '-0.005em',
              boxShadow: T.redGlowCTA, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {isLast ? t('tour_done') : t('tour_next')}
              <span style={{ fontSize: 14, lineHeight: 1 }}>→</span>
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
